import { useLocale } from 'next-intl'
import { Fragment, memo, useMemo } from 'react'
import { SeparationLine } from '@/components/separation-line'
import { Rating } from '@/features/reviews/rating'
import { ICustomerReview } from '@/features/reviews/review-types'
import { cn } from '@/utils/styles/cn'

interface ICustomerReviewProps {
    review: ICustomerReview
    type: 'preview' | 'full'
    className?: string
    textClassName?: string
    badgeClassName?: string
    customerNameClassName?: string
    separationLineClassName?: string
    textBodyClassName?: string
    titleClassName?: string
}

export const CustomerReview = memo(
    ({
        review,
        type,
        className,
        textClassName = 'text-black',
        badgeClassName = 'bg-black/10',
        customerNameClassName,
        separationLineClassName,
        textBodyClassName,
        titleClassName,
    }: ICustomerReviewProps) => {
        const locale = useLocale()

        const readableDate = useMemo(() => {
            if (review.readableDate) return review.readableDate

            if (!review.dateTime) return

            return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
                new Date(review.dateTime)
            )
        }, [review.dateTime, locale, review.readableDate])

        const customerName = useMemo(() => {
            if (!review.name) return ''

            return review.name.trim()
        }, [review.name])

        return (
            <div className={cn('flex flex-col', className)}>
                {customerName && (
                    <div className="flex items-center gap-3 mb-2">
                        <div
                            className={cn(
                                `rounded-full min-h-7 min-w-7 w-fit flex items-center justify-center text-[16px] font-bold
                                text-black/60`,
                                textClassName,
                                badgeClassName
                            )}
                        >
                            {customerName[0]}
                        </div>

                        <h5
                            className={cn(
                                'font-bold text-black whitespace-nowrap text-[14px]',
                                textClassName,
                                customerNameClassName
                            )}
                        >
                            {customerName}
                        </h5>
                    </div>
                )}

                <div className="flex items-center gap-3">
                    <Rating
                        withReadableRating={false}
                        rating={review.rating}
                        starsStyleType="maximal"
                        starClassName="w-4 h-4"
                        starsClassName="gap-[2px]"
                        ratingCountStyleType="only-count"
                        containerClassName="relative top-[-2px]"
                    />

                    {readableDate && (
                        <Fragment>
                            <SeparationLine className={separationLineClassName} />
                            <span className={cn('text-black/70 text-[13px]', textClassName)}>
                                {readableDate}
                            </span>
                        </Fragment>
                    )}
                </div>

                {review.title && (
                    <h5
                        className={cn(
                            'font-semibold mt-2 whitespace-nowrap',
                            textClassName,
                            titleClassName
                        )}
                    >
                        {review.title}
                    </h5>
                )}

                {review.body && type === 'full' && (
                    <p className={cn('mt-1 text-[15px] mb-2', textClassName, textBodyClassName)}>
                        {review.body}
                    </p>
                )}
            </div>
        )
    }
)

CustomerReview.displayName = 'CustomerReview'
